/**
 * TagsManagement - Página Completa de Gestão de Tags
 * Interface principal para administração de tags do sistema
 */

import React, { useState, useEffect } from 'react';
import { Tag as TagIcon, Folder, Users, TrendingUp, Plus, Search, Filter, Sparkles, Activity } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

import TagManager from '../components/TagManager.tsx';
import CategoryManager from '../components/CategoryManager.tsx';
import { getClinicInfo } from '../services/api.ts';

const TagsManagement: React.FC = () => {
  const { isDark } = useTheme();
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('tags');

  // Estrutura de tabs seguindo padrão CRM_v3
  const tabsList = [
    { key: 'tags', label: 'Tags', icon: TagIcon, shortLabel: 'Tags' },
    { key: 'categories', label: 'Categorias', icon: Folder, shortLabel: 'Categorias' }
  ];

  // Carregar informações da clínica atual
  const loadClinicInfo = async () => {
    try {
      setLoading(true);
      const clinic = await getClinicInfo();

      if (!clinic || !clinic.name_clinic) {
        throw new Error('Clínica não encontrada ou não selecionada');
      }

      // Extrair ID do localStorage ou usar padrão
      const clinicId = Number(localStorage.getItem('clinic_id')) || 1;
      setClinicId(clinicId);

      console.log(`✅ Clínica carregada: ${clinic.name_clinic} (ID: ${clinicId})`);
    } catch (error: any) {
      const errorMessage = error.message || 'Erro ao carregar informações da clínica';
      setError(errorMessage);
      console.error('❌ Erro ao carregar clínica:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClinicInfo();
  }, []);

  // Estado de loading
  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-sminy-primary border-t-transparent rounded-full animate-spin" />
          <p className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Carregando sistema de tags...</p>
        </div>
      </div>
    );
  }

  // Estado de erro
  if (error || !clinicId) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`text-center max-w-md mx-auto p-8 rounded-2xl shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-red-900/20' : 'bg-red-100'}`}>
            <TagIcon className={`w-8 h-8 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Erro ao Carregar
          </h2>
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {error || 'Não foi possível identificar a clínica atual.'}
          </p>
          <button
            onClick={loadClinicInfo}
            className="px-6 py-2.5 bg-sminy-primary text-white font-medium rounded-xl hover:bg-sminy-primary-hover transition-colors shadow-lg shadow-sminy-primary/20"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50/50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


        {/* Header Card estilo HubSpot */}
        <div className={`relative rounded-2xl border p-6 shadow-sm mb-6 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-slate-100 text-slate-600'
              }`}>
              <TagIcon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className={`text-xl md:text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Gestão de Tags
                </h1>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Organize e categorize seus contatos com eficiência
              </p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className={`rounded-2xl shadow-sm border p-1.5 mb-6 inline-flex w-full sm:w-auto ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
          {tabsList.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                className={`flex-1 sm:flex-none px-6 py-2.5 text-sm font-medium rounded-xl flex items-center justify-center space-x-2 transition-all duration-200 ${isActive
                  ? 'bg-sminy-primary/10 text-sminy-primary shadow-sm ring-1 ring-sminy-primary/20'
                  : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                onClick={() => setActiveTab(tab.key)}
              >
                <tab.icon className={`w-4 h-4 ${isActive ? 'text-sminy-primary' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className={`rounded-2xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="p-6">
            {activeTab === 'tags' && (
              <TagManager
                clinicId={clinicId}
                mode="full"
              />
            )}

            {activeTab === 'categories' && (
              <CategoryManager
                clinicId={clinicId}
              />
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className={`mt-8 rounded-2xl border shadow-sm p-6 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-slate-100 text-slate-600'
              }`}>
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Dicas de Uso
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Herança Automática:</strong> Tags de contatos são herdadas por leads.</span>
                  </p>
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Categorias:</strong> Use "Leads", "Vendas" para organizar por funil por exemplo.</span>
                  </p>
                </div>
                <div className="space-y-3">
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Analytics:</strong> Acompanhe o uso de tags.</span>
                  </p>
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Integração:</strong> Disponível no Chat e CRM.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagsManagement;